/**
 * OrgSwitcher — top-right "I'm in <org>" chip with click-to-switch popover.
 *
 * Placement: rendered inside TopNav's Toolbar, just left of the
 * notifications bell. Visible on every page (including Dashboard, where
 * the SideMenu is hidden), so the user always knows which workspace
 * they're in and can switch without hunting through a 3-deep menu.
 *
 * Behavior:
 *   - On mount, fetches GET /me/organizations (cheap — cross-org
 *     endpoint, no X-Organization-Id required).
 *   - Resolves the current org from localStorage
 *     (`current_organization_id`).
 *   - Chip label = current org name; click opens a popover.
 *   - Each row in the popover is an org. Current org has a checkmark
 *     and is disabled. Picking a different org:
 *        setCurrentOrgId(that.id) + window.location.reload()
 *     A full reload is deliberate: it flushes the in-memory data for
 *     the old org so no stale department / worker lists leak into
 *     the new context. Cheaper than wiring a cross-cutting
 *     "invalidate everything" signal through React Query / RSC.
 *   - Bottom of the popover: "Manage organizations" link → OrgGate
 *     picker for the full card UI (create-new / role info / plan
 *     info), plus a "Create new organization" shortcut.
 *
 * Graceful degradation:
 *   - Zero orgs (shouldn't normally happen post-OrgGate) → chip hidden.
 *   - Fetch fails → chip shows "Workspace" with no list, still
 *     clickable to bounce to OrgGate.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { landingPathFor } from '../../hooks/useRoleLanding';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ApartmentOutlined,
  CheckOutlined,
  KeyboardArrowDown,
  SupportAgentOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';

import {
  getCurrentOrgId,
  organizationApi,
  setCurrentOrgId,
  type Schema,
} from '../../api';
import { openSupportCase } from '../support/SupportCaseDialog';
import {
  filterPortalOrganizations,
  hasPortalRoleCode,
} from '../../hooks/useRoleLanding';
import { useTranslation } from 'react-i18next';

type OrgSummary = Schema<'OrganizationSummary'>;

// Privacy audit Wave 5 polish 2026-05-22 — role indicator helpers.
// Cross-org users with different roles per org need to see their role
// at a glance to avoid the "wait, am I admin or worker now?" confusion
// when they switch. Color-coded chip per role tier:
//   admin / super_admin / hr_admin → red ('error') — full control
//   manager / hr_manager           → yellow ('warning') — partial control
//   worker (default)               → green ('success') — self-service
//   anything else / null           → grey ('default')
const ROLE_CHIP_COLOR: Record<
  string,
  'error' | 'warning' | 'success' | 'default' | 'primary'
> = {
  super_admin: 'error',
  admin: 'error',
  hr_admin: 'error',
  manager: 'warning',
  hr_manager: 'warning',
  worker: 'success',
};

function roleChipColor(roleCode: string | null | undefined) {
  if (!roleCode) return 'default' as const;
  return ROLE_CHIP_COLOR[roleCode] ?? 'default';
}

function roleLabel(roleCode: string | null | undefined): string {
  if (!roleCode) return '—';
  // Convert snake_case → Title Case for display.
  return roleCode
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

// Toast key — set in sessionStorage right before a reload-driven
// org switch. The new app shell (TopNav.tsx) reads + clears it on
// mount and shows a Snackbar so the user gets a clear "Switched to
// X — you're now Y" signal that survives the page reload.
const SWITCH_TOAST_KEY = 'svdy_pending_org_switch_toast';

const OrgSwitcher: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<OrgSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const currentOrgId = useRef<string | null>(getCurrentOrgId());

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await organizationApi.listMyOrganizations();
      setOrgs(filterPortalOrganizations(rows));
    } catch (e) {
      // Non-fatal: the chip still works as a shortcut to OrgGate.
      // NOT localized: this is a developer log, not user copy. The sweep that routed
      // 194 sites through translateApiError converted this one too, which pulled `t`
      // into the useCallback's dependency array and broke the build — the right fix
      // is that a console line keeps the raw error.
      console.warn('[OrgSwitcher] listMyOrganizations failed', e);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const currentOrg = useMemo(
    () => orgs?.find((o) => o.id === currentOrgId.current) ?? null,
    [orgs],
  );

  const sortedOthers = useMemo(() => {
    if (!orgs) return [];
    return orgs
      .filter((o) => o.id !== currentOrgId.current && hasPortalRoleCode(o.role_code))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );
  }, [orgs]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
  };
  const handleClose = () => setAnchor(null);

  const handlePick = (org: OrgSummary) => {
    handleClose();
    if (!hasPortalRoleCode(org.role_code)) return;
    if (org.id === currentOrgId.current) return;
    setCurrentOrgId(org.id, org.role_code);
    // Privacy audit Wave 5 polish 2026-05-22 — write a toast hint to
    // sessionStorage so the post-reload TopNav can render a clear
    // "Switched to X — now acting as Y" message. Pre-fix the switch
    // was silent: cross-org users (the 1-2% who have different roles
    // per org) didn't realize the layout had changed.
    try {
      sessionStorage.setItem(
        SWITCH_TOAST_KEY,
        JSON.stringify({
          orgName: org.name,
          roleCode: org.role_code ?? null,
        }),
      );
    } catch {
      // sessionStorage might be disabled (incognito); silently skip.
    }
    // LAND, don't reload. `window.location.reload()` reloads the CURRENT url, so
    // switching from org A's /attendance/reports into an org without a QuTime
    // license reloaded that same path — a page the new org is not entitled to. The
    // admin then met a permission or license error instead of their new workspace,
    // and the copy for that state assumes you navigated there on purpose.
    //
    // Even when the path IS reachable it is the wrong destination: a report scoped
    // to org A's departments means nothing in org B, and a worker detail page
    // points at a row that does not exist there. Switching workspace is a change
    // of context, not of page.
    //
    // Still a full document load rather than a client-side navigate, for the
    // original reason — it flushes every in-memory cache holding the old org's
    // data, which is cheaper than wiring an invalidate-everything signal.
    window.location.href = landingPathFor(org.role_code);
  };

  const handleManageOrgs = () => {
    handleClose();
    // Intentionally DO NOT clear the current org id here — if the user
    // cancels out of OrgGate, keeping the cached id means
    // PrivateRoute doesn't kick them back to this page on the next
    // render. The ?manage=1 query flag forces OrgGate into picker
    // mode even for single-org users so this button always shows the
    // list.
    window.location.assign('/auth/select-org?manage=1');
  };

  /**
   * Open a support case pre-filled with the "delete organization"
   * category. Permanently deleting an org lives in the SVDY Platform
   * Operator console (ADR-009) — tenant admins can't self-serve it,
   * which is what this flow bridges.
   */
  const handleRequestDeletion = () => {
    handleClose();
    const orgName = currentOrg?.name ?? 'my organization';
    openSupportCase({
      category: 'organization-delete',
      subject: `Request to delete "${orgName}"`,
      description:
        `Please permanently delete the organization "${orgName}"` +
        (currentOrg ? ` (slug: ${currentOrg.slug}, id: ${currentOrg.id})` : '') +
        '.\n\nReason: ',
      priority: 'normal',
    });
  };

  // If we genuinely can't figure out any org yet, render a loading shell
  // so the toolbar width stays stable.
  if (loading && !orgs) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  const chipLabel =
    currentOrg?.name ??
    (orgs && orgs.length === 0
      ? t('common:orgSwitcher.noOrganization')
      : t('common:orgSwitcher.workspace'));

  return (
    <>
      <Chip
        icon={<ApartmentOutlined sx={{ fontSize: 16 }} />}
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {chipLabel}
            </Typography>
            {/* Inline role chip on the trigger button (Path C 2026-05-22) —
                cross-org users with different roles per org need to see
                their current role at a glance without opening the menu.
                Same color scheme as the menu rows below: admin/super_admin
                /hr_admin → red, manager/hr_manager → yellow, worker → green.
                Hidden for non-org states (Workspace / No organization). */}
            {currentOrg?.role_code && (
              <Chip
                label={roleLabel(currentOrg.role_code)}
                size="small"
                color={roleChipColor(currentOrg.role_code)}
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  flexShrink: 0,
                  // The outer Chip already has its own bg/border; the
                  // inner role chip sits flush against the name. Drop
                  // the role chip's own background so it doesn't clash
                  // with the trigger's white fill.
                  bgcolor: 'transparent',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Stack>
        }
        onClick={handleOpen}
        clickable
        variant="outlined"
        sx={{
          mx: { xs: 0.5, sm: 1 },
          height: 30,
          borderColor: 'rgba(0,0,0,0.2)',
          bgcolor: '#ffffff',
          '&:hover': { bgcolor: 'grey.50', borderColor: 'rgba(0,0,0,0.35)' },
          '& .MuiChip-icon': {
            color: theme.palette.primary.main,
            ml: 0.75,
          },
        }}
      />

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            minWidth: 280,
            maxWidth: 340,
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.2)',
              borderBottom: 'none',
              borderRight: 'none',
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              fontSize: '0.68rem',
            }}
          >
            {t('common:orgSwitcher.currentWorkspace')}
          </Typography>
        </Box>

        {/* Current org row — checkmark, not clickable */}
        {currentOrg ? (
          <MenuItem
            disabled
            sx={{
              py: 1,
              opacity: '1 !important',
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <CheckOutlined
              fontSize="small"
              color="primary"
              sx={{ mr: 1.25 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Privacy audit Wave 5 polish (2026-05-22) — vertical
                  layout: org name on its own line (full width, truncates
                  with ellipsis on long names), then slug + role chip on
                  the metadata line with chip right-aligned. Pre-fix had
                  chip inline after the name → chip position drifted
                  with name length, looking ragged across the menu. */}
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
                noWrap
              >
                {currentOrg.name}
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 0 }}
                  noWrap
                >
                  /{currentOrg.slug}
                </Typography>
                {currentOrg.role_code && (
                  <Chip
                    label={roleLabel(currentOrg.role_code)}
                    size="small"
                    color={roleChipColor(currentOrg.role_code)}
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                  />
                )}
              </Stack>
            </Box>
          </MenuItem>
        ) : (
          <MenuItem disabled sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('common:orgSwitcher.noWorkspaceSelected')}
            </Typography>
          </MenuItem>
        )}

        {sortedOthers.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'text.secondary',
                  fontSize: '0.68rem',
                }}
              >
                {t('common:orgSwitcher.switchTo')}
              </Typography>
            </Box>
            {sortedOthers.map((org) => (
              <MenuItem
                key={org.id}
                onClick={() => handlePick(org)}
                sx={{ py: 1 }}
              >
                <ApartmentOutlined
                  fontSize="small"
                  sx={{ mr: 1.25, color: 'text.secondary' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Same vertical layout as the current-org row above —
                      name primary line (truncates), slug + chip on the
                      metadata line with chip right-aligned for visual
                      consistency across rows of varying name lengths. */}
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                    noWrap
                  >
                    {org.name}
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 0 }}
                      noWrap
                    >
                      /{org.slug}
                    </Typography>
                    {org.role_code && (
                      <Chip
                        label={roleLabel(org.role_code)}
                        size="small"
                        color={roleChipColor(org.role_code)}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                      />
                    )}
                  </Stack>
                </Box>
              </MenuItem>
            ))}
          </>
        )}

        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleManageOrgs} sx={{ py: 1 }}>
          <SwapHorizOutlined
            fontSize="small"
            sx={{ mr: 1.25, color: 'text.secondary' }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('common:orgSwitcher.manageOrganizations')}
          </Typography>
        </MenuItem>
        <Tooltip
          title={t('common:orgSwitcher.requestDeletionTooltip')}
          placement="left"
        >
          <MenuItem onClick={handleRequestDeletion} sx={{ py: 1 }}>
            <SupportAgentOutlined
              fontSize="small"
              sx={{ mr: 1.25, color: 'text.secondary' }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('common:orgSwitcher.requestDeletion')}
            </Typography>
          </MenuItem>
        </Tooltip>
      </Menu>
    </>
  );
};

export default OrgSwitcher;
export { SWITCH_TOAST_KEY, roleLabel, roleChipColor };
